import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errText, cacheGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2 } from "lucide-react";

const STAGES = ["applied", "interview", "offered", "hired"];

export function ApplyForm() {
  const { trackId } = useParams();
  const [form, setForm] = useState({ phone: "", city: "", preference: "volunteer" });
  const [busy, setBusy] = useState(false);
  const { t } = useLang();
  const navigate = useNavigate();
  const input = "w-full h-12 px-4 rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/applications", { track_id: trackId, ...form });
      toast.success(t("applied"));
      navigate("/applications");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-4">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{t("applyToFscd")}</h1>
      <input data-testid="apply-phone-input" className={input} placeholder={t("phone")} value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })} required pattern="[0-9+ ]{10,15}" />
      <input data-testid="apply-city-input" className={input} placeholder={t("city")} value={form.city}
        onChange={(e) => setForm({ ...form, city: e.target.value })} required />
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">{t("preference")}</p>
        <div className="grid grid-cols-3 gap-2">
          {["volunteer", "intern", "fulltime"].map((p) => (
            <button type="button" key={p} data-testid={`preference-${p}-btn`} onClick={() => setForm({ ...form, preference: p })}
              className={`h-12 rounded-xl border font-semibold text-sm transition-colors ${form.preference === p ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-600"}`}>
              {t(p)}
            </button>
          ))}
        </div>
      </div>
      <button data-testid="submit-application-btn" disabled={busy}
        className="w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
        {t("submitApplication")}
      </button>
    </form>
  );
}

export function Applications() {
  const [apps, setApps] = useState(null);
  const { t, pick } = useLang();

  useEffect(() => { api.get("/me/applications").then((r) => setApps(r.data)).catch(() => setApps([])); }, []);

  if (!apps) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{t("applications")}</h1>
      {apps.length === 0 && <p data-testid="no-applications-msg" className="text-slate-500">{t("noApplications")}</p>}
      {apps.map((a) => {
        const idx = STAGES.indexOf(a.status);
        return (
          <div key={a.id} data-testid={`application-card-${a.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="font-heading font-bold text-slate-900">{pick(a.track_title)}</h2>
              <span className="text-xs font-semibold uppercase tracking-wider bg-teal-100 text-teal-800 px-3 py-1.5 rounded-full">
                {t(a.status)}{a.status === "hired" && a.hired_type ? ` — ${t(a.hired_type)}` : ""}
              </span>
            </div>
            <div className="flex items-center">
              {STAGES.map((s, i) => (
                <div key={s} className="flex-1 flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i <= idx ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {i < idx ? <CheckCircle2 size={16} /> : i + 1}
                  </div>
                  {i < STAGES.length - 1 && <div className={`flex-1 h-1 ${i < idx ? "bg-emerald-600" : "bg-slate-100"}`} />}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              {STAGES.map((s) => <span key={s}>{t(s)}</span>)}
            </div>
            {a.interview_at && (
              <p className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
                <CalendarClock size={16} /> {t("interviewOn")}: {new Date(a.interview_at).toLocaleString()}
              </p>
            )}
            {a.notes && <p className="text-sm text-slate-500">{a.notes}</p>}
          </div>
        );
      })}
    </div>
  );
}
